import type { ElementType } from "react"

import { useTranslations } from "next-intl"

import type { Product } from "@/components/marketplace/types"
import { ArrowIcon, Cents, Heading, Text } from "@/components/ui"
import { TileImage } from "@/components/ui/tile-image"
import { Link } from "@/i18n/navigation"
import { formatMoney, formatMoneyRounded } from "@/lib/money"

export type BentoTileKind = "category" | "product" | "stat" | "promo"

export interface BentoTile {
  id: string
  kind: BentoTileKind
  // Footprint on the mosaic: 1 square, 2 wide (2×1), 3 tall (1×2), 4 feature (2×2).
  span?: 1 | 2 | 3 | 4
  label?: string
  image?: string
  href?: string
  product?: Product
  big?: string
  sub?: string
  eyebrow?: string
  title?: string
  accent?: string
  serveId?: string
}

// The worlds mosaic. Every footprint is a full-width banner on the 2-col mobile
// grid so the stack never leaves a hole; from @xl each claims its cell — wide
// keeps one row, tall climbs two, feature fills a 2×2 corner. Dense flow packs
// the squares in around them, so an in-order authoring reads as the designed grid.
const FOOTPRINT: Record<1 | 2 | 3 | 4, string> = {
  1: "col-span-1 aspect-square",
  2: "col-span-2 aspect-banner",
  3: "col-span-2 aspect-banner @xl:col-span-1 @xl:row-span-2 @xl:aspect-auto",
  4: "col-span-2 aspect-banner @xl:row-span-2 @xl:aspect-auto",
}

const CELL_WEIGHT: Record<1 | 2 | 3 | 4, number> = { 1: 1, 2: 2, 3: 2, 4: 4 }

const FETCH_COLUMNS: Record<1 | 2 | 3 | 4, 1 | 2> = { 1: 1, 2: 2, 3: 2, 4: 2 }

const MOBILE_GUTTER = 16
const DESKTOP_GUTTER = 32
const MOBILE_GAP = 12
const DESKTOP_GAP = 16

const columnWidth = (gutter: number, gap: number, cols: number, spanned: number) => {
  const track = `calc((100vw - ${2 * gutter + (cols - 1) * gap}px) / ${cols})`
  return spanned === 1 ? track : `calc(${track} * ${spanned} + ${(spanned - 1) * gap}px)`
}

const tileSizes = (span: 1 | 2 | 3 | 4, cols: 3 | 4): string => {
  const spanned = FETCH_COLUMNS[span]
  const mobile = span === 1 ? columnWidth(MOBILE_GUTTER, MOBILE_GAP, 2, 1) : columnWidth(MOBILE_GUTTER, MOBILE_GAP, 2, 2)
  return [
    `(min-width: 768px) ${columnWidth(DESKTOP_GUTTER, DESKTOP_GAP, cols, spanned)}`,
    `(min-width: 608px) ${columnWidth(MOBILE_GUTTER, DESKTOP_GAP, cols, spanned)}`,
    mobile,
  ].join(", ")
}

const DESKTOP_COLS: Record<3 | 4, string> = {
  3: "@xl:grid-cols-3",
  4: "@xl:grid-cols-4",
}

interface TileProps {
  tile: BentoTile
  radius: string
  sizes: string
}

const tileFrame = "press focus-visible:ring-palette-bar group relative focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"

const TILE_RADIUS: Record<1 | 2 | 3 | 4, string> = {
  1: "rounded-12x",
  2: "rounded-16x",
  3: "rounded-16x",
  4: "rounded-20x",
}

// The only thing allowed on the photograph, because it is the affordance and not
// the copy. It points right, not up-right: ↗ is the glyph for leaving the site.
const WorldArrow = () => (
  <span
    aria-hidden
    className="bg-surface text-fg-strong shadow-floating duration-quick ease-productive absolute right-300 bottom-300 z-10 grid size-11 place-items-center rounded-full transition-transform motion-safe:group-hover:scale-108 @xl:right-400 @xl:bottom-400 @xl:size-13"
  >
    <ArrowIcon size={18} />
  </span>
)

// The label goes under the frame. Copy on the photograph belongs to the square
// bleeding mosaic; this rounded inset register captions outside it, as .world-cap
// does — and two of these tiles use the very files the worlds section leaves bare.
const CategoryTile = ({ tile, radius, sizes }: TileProps) => (
  <>
    <span className={`${radius} bg-surface-newgray relative block min-h-30 flex-1 overflow-hidden`}>
      {tile.image && (
        <TileImage
          src={tile.image}
          alt={tile.label ?? ""}
          fill
          sizes={sizes}
          className="duration-moderate ease-productive object-cover transition-transform motion-safe:group-hover:scale-105"
        />
      )}
      {tile.href && <WorldArrow />}
    </span>
    <span className="flex flex-wrap items-baseline gap-x-250 gap-y-50 pt-300">
      <Heading as="span" variant="sm" family="display" tone="strong" balance className="@xl:text-heading-md font-extrabold">
        {tile.label}
      </Heading>
      {(tile.eyebrow || tile.sub) && (
        <Text as="span" variant="caption" family="text" tone="muted" className="font-medium">
          {tile.eyebrow ?? tile.sub}
        </Text>
      )}
    </span>
  </>
)

const ProductTile = ({ tile, sizes }: TileProps) => {
  const t = useTranslations("marketplace.bnpl")
  const product = tile.product
  if (!product) return null
  return (
    <div className="bg-surface flex h-full flex-col">
      <div className="bg-surface-newgray relative flex-1 overflow-hidden">
        <TileImage
          src={product.image}
          alt={product.title}
          fill
          sizes={sizes}
          className="duration-moderate ease-productive object-cover transition-transform motion-safe:group-hover:scale-105"
        />
      </div>
      <div className="flex flex-col gap-50 p-300 @xl:p-350">
        {product.brand && (
          <Text as="span" variant="eyebrow" tone="muted">
            {product.brand}
          </Text>
        )}
        <Text as="span" variant="bodySm" family="display" tone="default" className="line-clamp-1 font-bold">
          {product.title}
        </Text>
        <span className="font-display text-price text-fg font-extrabold tabular-nums">{formatMoneyRounded(product.price)}</span>
        {product.payments != null && (
          <Text as="span" variant="caption" family="text" tone="subtle">
            <Cents>{t("orInstallments", { count: product.installments ?? 4, amount: formatMoney(product.payments) })}</Cents>
          </Text>
        )}
      </div>
    </div>
  )
}

const StatTile = ({ tile }: TileProps) => (
  <div className="bg-palette-soft text-palette-on-soft flex h-full flex-col items-center justify-center gap-100 p-400 text-center">
    <Heading as="span" variant="displaySm" family="display" tone="inherit" className="text-palette-accent @xl:text-display-lg font-extrabold">
      {tile.big}
    </Heading>
    {tile.sub && (
      <Text as="span" variant="bodySm" family="display" tone="subtle" balance className="max-w-measure-sm font-semibold">
        {tile.sub}
      </Text>
    )}
  </div>
)

const PromoTile = ({ tile, radius, sizes }: TileProps) => (
  <>
    <span className={`${radius} bg-surface-newgray relative block min-h-30 flex-1 overflow-hidden`}>
      {tile.image && (
        <TileImage src={tile.image} alt="" fill sizes={sizes} className="duration-moderate ease-productive object-cover transition-transform motion-safe:group-hover:scale-105" />
      )}
      {tile.href && <WorldArrow />}
    </span>
    <span className="flex flex-wrap items-baseline gap-x-250 gap-y-50 pt-300">
      <Heading as="span" variant="sm" family="display" tone="strong" balance className="@xl:text-heading-md font-extrabold">
        {tile.title}
        {tile.accent && <span className="text-fg-brand"> {tile.accent}</span>}
      </Heading>
      {tile.eyebrow && (
        <Text as="span" variant="caption" family="text" tone="muted" className="font-medium">
          {tile.eyebrow}
        </Text>
      )}
    </span>
  </>
)

const TILE_BY_KIND = {
  category: CategoryTile,
  product: ProductTile,
  stat: StatTile,
  promo: PromoTile,
} as const

export const Bento = ({ tiles }: { tiles: BentoTile[] }) => {
  // Pick the column count the footprint weights divide evenly, so dense flow
  // closes the mosaic without an empty cell; the square tiles size the rows.
  const cells = tiles.reduce((sum, tile) => sum + CELL_WEIGHT[tile.span ?? 1], 0)
  const desktopCols = (cells % 4 === 0 ? 4 : 3) as 3 | 4
  return (
    <div className="@container">
      <div className={`grid grid-flow-row-dense grid-cols-2 gap-300 @xl:gap-400 ${DESKTOP_COLS[desktopCols]}`}>
        {tiles.map((tile) => {
          const TileBody = TILE_BY_KIND[tile.kind]
          const Element: ElementType = tile.href ? Link : "div"
          const span = tile.span ?? 1
          const radius = TILE_RADIUS[span]
          const captioned = tile.kind === "category" || tile.kind === "promo"
          return (
            <Element
              key={tile.id}
              {...(tile.href ? { href: tile.href } : {})}
              data-nelo-item={tile.kind === "product" ? tile.product?.id : undefined}
              data-nelo-serve={tile.serveId}
              className={`${tileFrame} ${FOOTPRINT[span]} ${captioned ? "flex flex-col" : `${radius} overflow-hidden`}`}
            >
              <TileBody tile={tile} radius={radius} sizes={tileSizes(span, desktopCols)} />
            </Element>
          )
        })}
      </div>
    </div>
  )
}
